import differenceInDays from 'date-fns/differenceInDays';
import isValid from 'date-fns/isValid';
import parseISO from 'date-fns/parseISO';
import subDays from 'date-fns/subDays';

import { fg } from '@atlaskit/platform-feature-flags/fg';

import {
	formatIsoLocalDate,
	formatMonthOnlyDate,
	formatNiceDate,
	formatNiceIntervalRangeDate,
	formatNiceMonthDate,
	formatNiceQuarterDate,
} from '@atlassian/townsquare-date-utils/date-format';

import type {
	CalendarProps,
	CalendarState,
	DatePickerValue,
	IntervalOption,
	TargetDate,
} from '../../types';

export const DateRangeScale = {
	DAY: 'DAY',
	MONTH: 'MONTH',
	QUARTER: 'QUARTER',
	INTERVAL: 'INTERVAL',
} as const;

export type DateRangeScale = (typeof DateRangeScale)[keyof typeof DateRangeScale];

const MONTH_THRESHOLD = 31;
const DAY_THRESHOLD = 1;

export const getDateRangeScale = (dateObj?: DatePickerValue): 'DAY' | 'MONTH' | 'QUARTER' => {
	if (dateObj) {
		const dateDiff = differenceInDays(new Date(dateObj.end), new Date(dateObj.start));
		if (dateDiff < DAY_THRESHOLD) {
			return DateRangeScale.DAY;
		}
		if (dateDiff <= MONTH_THRESHOLD) {
			return DateRangeScale.MONTH;
		}
		return DateRangeScale.QUARTER;
	}
	// If date is somehow undefined, we'll show quarters picker by default
	return DateRangeScale.QUARTER;
};

const DEFAULT_FISCAL_YEAR_PREFIX = 'FY';

export const renderDateString = (
	locale: Locale,
	dateString?: DatePickerValue,
	placeholder?: string,
	withTimeScale?: boolean,
	intervalDisplayName?: string,
	fiscalYearPrefix?: string,
): string | undefined => {
	if (!dateString) {
		return placeholder;
	}
	const endDate = parseISO(dateString.end);
	if (!isValid(endDate)) {
		return placeholder;
	}
	if (intervalDisplayName !== undefined) {
		const startDate = parseISO(dateString.start);
		if (!isValid(startDate)) {
			return placeholder;
		}
		return formatNiceIntervalRangeDate(
			fiscalYearPrefix ?? DEFAULT_FISCAL_YEAR_PREFIX,
			intervalDisplayName,
			startDate,
			endDate,
			locale,
		);
	}
	switch (getDateRangeScale(dateString)) {
		case DateRangeScale.DAY:
			return formatNiceDate(endDate, locale);
		case DateRangeScale.QUARTER:
			return formatNiceQuarterDate(endDate, locale);
		default:
		case DateRangeScale.MONTH:
			return withTimeScale
				? formatNiceMonthDate(endDate, locale)
				: formatMonthOnlyDate(endDate, locale);
	}
};

/** Map an interval to DatePickerValue (ISO local date strings) for a given display year and locale. */
export function intervalToDatePickerValue(
	interval: IntervalOption,
	displayYear: number,
	locale: Locale,
): DatePickerValue {
	const startInDisplayYear = new Date(
		displayYear,
		interval.startDate.getMonth(),
		interval.startDate.getDate(),
	);
	const endInDisplayYear = new Date(
		displayYear,
		interval.endDate.getMonth(),
		interval.endDate.getDate(),
	);
	return {
		start: formatIsoLocalDate(startInDisplayYear, locale),
		end: formatIsoLocalDate(endInDisplayYear, locale),
	};
}

export const getInitialMonthPickerDate = (props: CalendarProps): CalendarState => {
	const now = new Date();
	// Clamp "now" up to minDate so the default month is never earlier than the minimum selectable month when the browser and workspace timezones straddle a month boundary.
	const fallbackDate =
		props.minDate && fg('goal_types_m3_bundled_updates') && now < props.minDate
			? props.minDate
			: now;
	const parsedDate = props.date ? parseISO(props.date) : fallbackDate;
	return {
		initialYear: parsedDate.getFullYear(),
		year: parsedDate.getFullYear(),
		month: parsedDate.getMonth(),
	};
};

export const getMonthPickerDate = (date: string | undefined): CalendarState | undefined => {
	if (!date) {
		return undefined;
	}
	const parsedDate = parseISO(date);

	if (!isValid(parsedDate)) {
		return undefined;
	}

	return {
		initialYear: parsedDate.getFullYear(),
		year: parsedDate.getFullYear(),
		month: parsedDate.getMonth(),
	};
};

export { useDateLocale } from './date-locale';

export function isValidConfidence(
	confidence?: string,
): confidence is Exclude<DateRangeScale, 'INTERVAL'> {
	if (!confidence) {
		return false;
	}
	return ['MONTH', 'QUARTER', 'DAY'].includes(confidence);
}

/** Confidence scores per scale. INTERVAL excluded – intervals use explicit start/end only. */
export const fuzzyDateValue: {
	[key in Exclude<DateRangeScale, 'INTERVAL'>]: number;
} = {
	[DateRangeScale.DAY]: 100,
	[DateRangeScale.MONTH]: 0,
	[DateRangeScale.QUARTER]: -50,
};

export const getFuzzyScale = (confidence?: number | null): 'DAY' | 'MONTH' | 'QUARTER' => {
	// I hate JS. The number 0 is a false-y value. So we have to be explicit here for null checking
	if (confidence !== undefined && confidence !== null) {
		if (confidence >= fuzzyDateValue[DateRangeScale.DAY]) {
			return DateRangeScale.DAY;
		}
		if (confidence >= fuzzyDateValue[DateRangeScale.MONTH]) {
			return DateRangeScale.MONTH;
		}
		return DateRangeScale.QUARTER;
	} else {
		// If confidence is somehow undefined, we'll show full date just in case
		return DateRangeScale.DAY;
	}
};

// Conversion because DateRangePicker uses date ranges but we back-end uses date/confidence
export const convertTargetDateToRange = (
	targetDate: TargetDate | null,
):
	| {
			start: string;
			end: string;
	  }
	| undefined => {
	if (!targetDate?.date) {
		return undefined;
	}

	if (targetDate.confidence === 'DAY') {
		return {
			start: targetDate.date,
			end: targetDate.date,
		};
	}
	if (targetDate.confidence === 'MONTH') {
		return {
			// Threshold for daily view is one day difference
			start: subDays(new Date(targetDate.date), 1).toString(),
			end: targetDate.date,
		};
	}
	return {
		// We show the quarter view by default
		start: '',
		end: targetDate.date,
	};
};
